import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBuniChat, BuniChatWidget } from '@buni.ai/chatbot-react';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteChat } from '../contexts/ChatContext';
import ChatLauncher from './ChatLauncher';

const BUNI_ORIGIN = 'https://www.buni.ai';

// Matches the site's signal accent in each theme.
const ACCENT = { light: '#1D6B3F', dark: '#5CD68A' };

const BuniChatWidgetInstance = ({ chatTheme, onAutoOpenHandled, onOpenStateChange, shouldAutoOpen, token }) => {
    const hasOpenedRef = useRef(false);
    const accent = ACCENT[chatTheme] || ACCENT.light;

    const options = useMemo(() => ({
        token,
        config: {
            theme: chatTheme,
            primaryColor: accent,
            position: 'bottom-right',
            width: 370,
            height: 680,
            showMinimize: true,
            initialMinimized: true,
            allowMinimize: true,
            showTriggerText: false,
            hideDefaultTrigger: true,
        }
    }), [accent, chatTheme, token]);

    const { isLoaded, isOpen, open, hide, widget } = useBuniChat(options);
    const isButtonVisible = !isOpen;

    const { openRequest, pendingQuestion, clearPending, reportOpen, reportReady } = useSiteChat();
    const handledOpenRef = useRef(openRequest);

    useEffect(() => {
        onOpenStateChange(isOpen);
        reportOpen(isOpen);
    }, [isOpen, onOpenStateChange, reportOpen]);

    useEffect(() => {
        reportReady(isLoaded);
    }, [isLoaded, reportReady]);

    // Somewhere on the page asked us to open.
    useEffect(() => {
        if (openRequest === handledOpenRef.current) return;
        if (!isLoaded) return;
        handledOpenRef.current = openRequest;
        open();
    }, [isLoaded, open, openRequest]);

    // The chat frame accepts { type: 'sendMessage' } but never posts anything back
    // to the parent — no `buni:iframe_handshake_ack`, no events. That ack is exactly
    // what the SDK's own sendMessage() waits for, so routing through it leaves the
    // question queued forever. It is posted to the frame directly instead.
    //
    // With no readiness signal to wait on, delivery keys off the frame's load event
    // plus a settle window for its app to attach listeners. The frame is created on
    // page load, so by the time anyone clicks a question the window has long since
    // passed and delivery reads as instant.
    const frameLoadedAtRef = useRef(0);
    const [frameLoaded, setFrameLoaded] = useState(false);

    useEffect(() => {
        const frame = widget && widget.chatIframe;
        if (!frame) return undefined;

        let settled = false;
        const mark = () => {
            if (settled) return;
            settled = true;
            frameLoadedAtRef.current = Date.now();
            setFrameLoaded(true);
        };

        // Its load event, or a backstop for when it fired before we were listening.
        frame.addEventListener('load', mark);
        const backstop = setTimeout(mark, 2500);

        return () => {
            frame.removeEventListener('load', mark);
            clearTimeout(backstop);
        };
    }, [isLoaded, widget]);

    // clearPending() is what guarantees a question is delivered exactly once.
    useEffect(() => {
        if (!pendingQuestion || !frameLoaded) return undefined;
        const frame = widget && widget.chatIframe && widget.chatIframe.contentWindow;
        if (!frame) return undefined;

        const settleFor = Math.max(0, 2000 - (Date.now() - frameLoadedAtRef.current));
        const timer = setTimeout(() => {
            try {
                frame.postMessage({ type: 'sendMessage', data: { message: pendingQuestion } }, BUNI_ORIGIN);
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') console.warn('[ask] delivery failed', err);
            }
            clearPending();
        }, settleFor);

        return () => clearTimeout(timer);
    }, [clearPending, frameLoaded, pendingQuestion, widget]);

    useEffect(() => {
        if (isOpen) {
            hasOpenedRef.current = true;
            return;
        }

        if (isLoaded && hasOpenedRef.current) {
            hide();
        }
    }, [hide, isLoaded, isOpen]);

    return (
        <>
            <BuniChatWidget />
            {isButtonVisible && (
                <ChatLauncher onOpen={open} disabled={!isLoaded} />
            )}
        </>
    );
};

const BuniChatWidgetWrapper = () => {
    const token = process.env.REACT_APP_BUNI_CHAT_TOKEN || '';
    const { theme } = useTheme();
    const chatTheme = theme === 'dark' ? 'dark' : 'light';
    const previousThemeRef = useRef(chatTheme);
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);
    const [shouldAutoOpen, setShouldAutoOpen] = useState(false);

    useEffect(() => {
        if (previousThemeRef.current !== chatTheme) {
            if (isWidgetOpen) {
                setShouldAutoOpen(true);
            }

            previousThemeRef.current = chatTheme;
        }
    }, [chatTheme, isWidgetOpen]);

    if (!token) {
        return null;
    }

    return (
        <BuniChatWidgetInstance
            key={chatTheme}
            chatTheme={chatTheme}
            onAutoOpenHandled={() => setShouldAutoOpen(false)}
            onOpenStateChange={setIsWidgetOpen}
            shouldAutoOpen={shouldAutoOpen}
            token={token}
        />
    );
};

export default BuniChatWidgetWrapper;
